/**
 * Browser-based location detection utilities
 * Attempts to determine user's location without requiring permissions
 */

/**
 * Get country from timezone
 */
function getCountryFromTimezone(): string | null {
  try {
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone
    
    // Map common timezones to countries
    const timezoneToCountry: Record<string, string> = {
      'America/New_York': 'United States',
      'America/Chicago': 'United States',
      'America/Denver': 'United States',
      'America/Los_Angeles': 'United States',
      'America/Phoenix': 'United States',
      'America/Anchorage': 'United States',
      'America/Honolulu': 'United States',
      'America/Toronto': 'Canada',
      'America/Vancouver': 'Canada',
      'America/Mexico_City': 'Mexico',
      'Europe/London': 'United Kingdom',
      'Europe/Paris': 'France',
      'Europe/Berlin': 'Germany',
      'Europe/Rome': 'Italy',
      'Europe/Madrid': 'Spain',
      'Europe/Amsterdam': 'Netherlands',
      'Europe/Stockholm': 'Sweden',
      'Europe/Dublin': 'Ireland',
      'Europe/Athens': 'Greece',
      'Europe/Lisbon': 'Portugal',
      'Europe/Brussels': 'Belgium',
      'Europe/Vienna': 'Austria',
      'Europe/Zurich': 'Switzerland',
      'Europe/Warsaw': 'Poland',
      'Europe/Prague': 'Czech Republic',
      'Europe/Budapest': 'Hungary',
      'Europe/Helsinki': 'Finland',
      'Europe/Copenhagen': 'Denmark',
      'Europe/Oslo': 'Norway',
      'Asia/Tokyo': 'Japan',
      'Asia/Shanghai': 'China',
      'Asia/Hong_Kong': 'Hong Kong',
      'Asia/Singapore': 'Singapore',
      'Asia/Seoul': 'South Korea',
      'Asia/Dubai': 'United Arab Emirates',
      'Asia/Mumbai': 'India',
      'Asia/Bangkok': 'Thailand',
      'Asia/Jakarta': 'Indonesia',
      'Asia/Manila': 'Philippines',
      'Australia/Sydney': 'Australia',
      'Australia/Melbourne': 'Australia',
      'Australia/Brisbane': 'Australia',
      'Australia/Perth': 'Australia',
      'Pacific/Auckland': 'New Zealand',
      'America/Sao_Paulo': 'Brazil',
      'America/Buenos_Aires': 'Argentina',
      'America/Santiago': 'Chile',
      'America/Lima': 'Peru',
      'Africa/Johannesburg': 'South Africa',
      'Africa/Cairo': 'Egypt',
      'Africa/Lagos': 'Nigeria',
      'Africa/Nairobi': 'Kenya'
    }
    
    return timezoneToCountry[timezone] || null
  } catch {
    return null
  }
}

/**
 * Get country from browser locale
 */
function getCountryFromLocale(): string | null {
  try {
    const locale = navigator.language || (navigator as any).userLanguage
    
    // Extract country code from locale (e.g., "en-US" -> "US")
    const parts = locale.split('-')
    if (parts.length > 1) {
      const countryCode = parts[1].toUpperCase()
      
      // Map common country codes to country names
      const countryCodeMap: Record<string, string> = {
        'US': 'United States',
        'GB': 'United Kingdom',
        'CA': 'Canada',
        'AU': 'Australia',
        'NZ': 'New Zealand',
        'IE': 'Ireland',
        'ZA': 'South Africa',
        'IN': 'India',
        'SG': 'Singapore',
        'MY': 'Malaysia',
        'PH': 'Philippines',
        'ID': 'Indonesia',
        'TH': 'Thailand',
        'VN': 'Vietnam',
        'JP': 'Japan',
        'KR': 'South Korea',
        'CN': 'China',
        'HK': 'Hong Kong',
        'TW': 'Taiwan',
        'DE': 'Germany',
        'FR': 'France',
        'IT': 'Italy',
        'ES': 'Spain',
        'NL': 'Netherlands',
        'BE': 'Belgium',
        'CH': 'Switzerland',
        'AT': 'Austria',
        'SE': 'Sweden',
        'NO': 'Norway',
        'DK': 'Denmark',
        'FI': 'Finland',
        'PL': 'Poland',
        'CZ': 'Czech Republic',
        'HU': 'Hungary',
        'PT': 'Portugal',
        'GR': 'Greece',
        'BR': 'Brazil',
        'MX': 'Mexico',
        'AR': 'Argentina',
        'CL': 'Chile',
        'CO': 'Colombia',
        'PE': 'Peru',
        'AE': 'United Arab Emirates',
        'SA': 'Saudi Arabia',
        'IL': 'Israel',
        'TR': 'Turkey',
        'EG': 'Egypt',
        'NG': 'Nigeria',
        'KE': 'Kenya'
      }
      
      return countryCodeMap[countryCode] || null
    }
    
    return null
  } catch {
    return null
  }
}

/**
 * Get location from IP using a free geolocation service
 * Falls back gracefully if the service is unavailable
 */
async function getLocationFromIP(): Promise<{ country?: string; city?: string } | null> {
  try {
    // Try ipapi.co first (free tier: 1000 requests/day)
    const response = await fetch('https://ipapi.co/json/', {
      method: 'GET',
      headers: {
        'Accept': 'application/json'
      }
    })
    
    if (response.ok) {
      const data = await response.json()
      if (data.country_name && !data.error) {
        return {
          country: data.country_name,
          city: data.city
        }
      }
    }
  } catch (error) {
    // Silently fail - we'll try other methods
    console.debug('IP geolocation failed:', error)
  }
  
  // Fallback to ip-api.com (free tier: 45 requests/minute)
  try {
    const response = await fetch('http://ip-api.com/json/?fields=status,country,city', {
      method: 'GET'
    })
    
    if (response.ok) {
      const data = await response.json()
      if (data.status === 'success' && data.country) {
        return {
          country: data.country,
          city: data.city
        }
      }
    }
  } catch (error) {
    // Silently fail
    console.debug('IP geolocation fallback failed:', error)
  }
  
  return null
}

/**
 * Detect user's location using multiple browser-based methods
 * Returns the best guess for country (and optionally city)
 * 
 * Priority:
 * 1. IP-based geolocation (most accurate, no permission needed)
 * 2. Timezone-based inference
 * 3. Browser locale
 * 4. Default to "United States"
 */
export async function detectBrowserLocation(): Promise<{
  country: string
  city?: string
}> {
  // Try IP-based geolocation first (most accurate)
  const ipLocation = await getLocationFromIP()
  if (ipLocation?.country) {
    return {
      country: ipLocation.country,
      city: ipLocation.city
    }
  }
  
  // Fall back to timezone-based detection
  const timezoneCountry = getCountryFromTimezone()
  if (timezoneCountry) {
    return { country: timezoneCountry }
  }
  
  // Fall back to locale-based detection
  const localeCountry = getCountryFromLocale()
  if (localeCountry) {
    return { country: localeCountry }
  }
  
  // Final fallback
  return { country: 'United States' }
}

/**
 * Get location suggestions based on detected location
 * Returns an array of location strings (country, city if available)
 */
export async function getLocationSuggestions(): Promise<string[]> {
  const location = await detectBrowserLocation()
  const suggestions: string[] = []
  
  if (location.city && location.country) {
    suggestions.push(location.city)
  }
  
  if (location.country) {
    suggestions.push(location.country)
  }
  
  return suggestions
}


